import { z } from 'zod';

/**
 * Contracten van het globale zoeken (Stap 34d).
 *
 * Eén zoekveld, drie soorten resultaten, en één regel die boven alles staat: **de zoekroute is
 * nooit een achterdeur**. Een resultaat verschijnt alleen als de vrager het onderliggende object
 * ook los mag openen. Dat is geen filter achteraf maar de reden dat elke soort zijn eigen query
 * doet, in de module die de rechten kent (`docs/conventions/zoeken.md`).
 */

/**
 * De drie soorten. Géén relevantieweging over de soorten heen: elke groep heeft zijn eigen
 * sortering, en "is deze collega belangrijker dan dit bericht" is een vraag zonder antwoord.
 */
export const searchKindSchema = z.enum(['post', 'colleague', 'document']);
export type SearchKind = z.infer<typeof searchKindSchema>;

/**
 * Eén treffer, in dezelfde vorm voor alle drie de soorten.
 *
 * Eén vorm en geen discriminated union, omdat de client er hetzelfde mee doet: een pictogram
 * kiezen op `kind`, de titel tonen, en navigeren op `kind` + `id`. Een aparte vorm per soort zou
 * drie weergaven betekenen die uit elkaar gaan lopen.
 */
export const searchHitSchema = z.object({
  kind: searchKindSchema,
  /** Het id waarmee de client navigeert: een bericht-id, persoon-id of document-id. */
  id: z.guid(),
  title: z.string(),
  /** De regel eronder: de afdeling van een collega, de map van een document, een datum. */
  subtitle: z.string().nullable(),
});
export type SearchHit = z.infer<typeof searchHitSchema>;

/**
 * Eén groep resultaten.
 *
 * `hasMore` en geen totaalaantal: een `count(*)` over alle treffers kost een tweede query over
 * dezelfde rechtenfilter, en het antwoord op "hoeveel zijn er precies" heeft niemand nodig —
 * "er zijn er meer" is wat de knop "alles bekijken" rechtvaardigt.
 */
export const searchGroupSchema = z.object({
  kind: searchKindSchema,
  items: z.array(searchHitSchema),
  hasMore: z.boolean(),
});
export type SearchGroup = z.infer<typeof searchGroupSchema>;

/**
 * Het antwoord.
 *
 * Een groep die **ontbreekt** betekent iets: die soort is er niet voor deze vrager — omdat hij het
 * recht mist, of omdat de module uit staat. Dat is bewust geen foutmelding en ook geen lege groep:
 * "Geen documenten gevonden" bij iemand die nooit documenten mag zien, is een mededeling over iets
 * wat niet bestaat.
 *
 * Een groep die er **wel** is maar leeg, betekent het andere: er is gezocht en er was niets.
 */
export const searchResponseSchema = z.object({
  /** De genormaliseerde zoekterm, zoals de server hem gebruikt heeft. */
  query: z.string(),
  /**
   * Of er werkelijk gezocht is. Onder twee tekens zoekt de server niet — een zoekopdracht op "a"
   * levert een halve personeelslijst op en kost een volledige tabelscan.
   */
  searched: z.boolean(),
  groups: z.array(searchGroupSchema),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

/** Hoeveel treffers er per groep standaard meegaan. */
export const SEARCH_LIMIT_DEFAULT = 5;
export const SEARCH_LIMIT_MAX = 25;
